import { casaDia, casaID, casaIgual, casaTexto } from "@/lib/filtros/texto";
import type { LotoListItem } from "@/lib/lotos";

/**
 * CLAVES_LOTOS son los parámetros que admite el índice de LOTOs EN EL SPA (#247). Desde
 * #250 son los MISMOS nueve del API: `creado_por` era el único que el servidor aceptaba y
 * el SPA no ofrecía, porque la fila del índice no publicaba quién abrió el LOTO. Ahora sí
 * (D4), así que hay de dónde sacar las opciones del desplegable —los creadores presentes en
 * la lista cargada, D3— y con qué reproducir el filtro sin red: por ID, el mismo predicado
 * que el `lo.creado_por = ?` del servidor.
 */
export const CLAVES_LOTOS = [
  "estado",
  "equipo",
  "area",
  "creado_por",
  "desde",
  "hasta",
  "cierre_desde",
  "cierre_hasta",
  "q",
] as const;

/** FiltrosLotos es el objeto de filtros del índice de LOTOs tal como viaja en la URL. */
export type FiltrosLotos = Partial<Record<(typeof CLAVES_LOTOS)[number], string>>;

/**
 * EquipoDeArea es lo mínimo que necesita el filtro por área sin red: el servidor filtra por
 * `equipos.id_area`, y la fila del índice solo trae el NOMBRE del área, así que el
 * equivalente exacto en el cliente es resolver qué equipos pertenecen a esa área con la
 * lista de equipos ya cacheada. Sin esa lista, un filtro por área no deja pasar nada
 * (mismo resultado que un id de otra obra, R4).
 */
export type EquipoDeArea = { id: number; id_area: number };

/**
 * filtrarLotos aplica los filtros en el navegador (modo sin red, D2). Debe dar el MISMO
 * resultado que el SQL del servidor: ver la tabla de casos compartida en
 * backend/internal/loto/filtros_integration_test.go (TestFiltrosLotosHTTP).
 *
 * El texto libre `q` cubre número, descripción, código y nombre del equipo y —desde #403— el
 * NOMBRE de quien abrió el LOTO y el de quien lo cerró: lo que la fila publica es lo que el
 * buscador encuentra (#289). No cubre el correo ni el nombre de usuario, que la fila no
 * publica (y que este lado, por tanto, ni recibe).
 */
export function filtrarLotos(
  filas: LotoListItem[],
  filtros: FiltrosLotos,
  equipos: EquipoDeArea[] = [],
): LotoListItem[] {
  // El servidor filtra por `equipos.id_area`; aquí el equivalente EXACTO es el conjunto de
  // equipos de esa área según la lista cacheada. Vacío ⇒ nada casa, igual que un id de otra
  // obra en el servidor (R4).
  const equiposDelArea =
    filtros.area === undefined || filtros.area === ""
      ? null
      : new Set(equipos.filter((e) => String(e.id_area) === filtros.area).map((e) => e.id));

  return filas.filter(
    (l) =>
      casaIgual(filtros.estado, l.estado) &&
      casaID(filtros.equipo, l.id_equipo) &&
      (equiposDelArea === null || equiposDelArea.has(l.id_equipo)) &&
      // #250 · por ID, igual que el `lo.creado_por = ?` del servidor: el nombre es lo que se
      // ve, pero comparar por texto no sería el mismo predicado (dos personas pueden
      // llamarse igual, y un LOTO sin autor tiene el nombre vacío, no un id vacío).
      casaID(filtros.creado_por, l.creado_por) &&
      casaDia(l.fecha_apertura, filtros.desde, filtros.hasta) &&
      casaDia(l.fecha_cierre, filtros.cierre_desde, filtros.cierre_hasta) &&
      // #403 · los dos nombres van DESPUÉS de las cuatro columnas de siempre y en el mismo
      // orden que el `OR` del servidor. `casaTexto` trata null/undefined como cadena vacía,
      // que es el `COALESCE(…, '')` del SQL: un LOTO sin creador o sin cerrador no necesita
      // ningún caso especial.
      casaTexto(
        filtros.q,
        l.numero_loto,
        l.descripcion,
        l.codigo_equipo,
        l.equipo,
        l.creado_por_nombre,
        l.cerrado_por_nombre,
      ),
  );
}
